import React, { useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useRole } from "../context/RoleContext";
import {
  submitApplication,
  resubmitApplication,
  uploadFile,
} from "../lib/applications";
import { Toaster, toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  FileText,
  Image as ImageIcon,
  Upload,
  X,
  AlertCircle,
  ShieldCheck,
  ExternalLink,
} from "lucide-react";
import {
  SERVICE_AREAS,
  ARCHITECT_TYPOLOGIES as TYPOLOGIES,
  DEMOGRAPHIC_FLAGS as FLAGS,
} from "../lib/architectTaxonomy";

const PRACTICE_MIN = 300;
const TAGLINE_MAX = 140;
const BIO_MAX = 2000;
const LIFESTYLE_MAX = 4;
const CREDENTIALS_MAX = 5;

// Static $250 fee invoiced on approval. Payment integration is post-MVP;
// for now we just communicate the amount and persist it on the record.
export const REGISTRATION_FEE_USD = 250;
const IPLA_LOOKUP_URL = "https://mylicense.in.gov/EVerification/Search.aspx";

export const STANDARD_CREDENTIALS = [
  {
    key: "leed_ap",
    label: "LEED AP",
    desc: "USGBC-accredited green-building professional.",
  },
  {
    key: "certified_passive_house_consultant",
    label: "Certified Passive House Consultant",
    desc: "PHIUS or PHI accredited.",
  },
  {
    key: "certified_green_professional",
    label: "Certified Green Professional",
    desc: "NAHB Certified Green Professional designation.",
  },
];

const EMPTY_FORM = {
  contact: { name: "", email: "", phone: "" },
  firm: "",
  license_no: "",
  license_verified: false,
  postal_address: { street: "", city: "", state: "IN", postal_code: "" },
  years_practising: "",
  service_areas: [],
  specialisations: [],
  practice_statement: "",
  demographic_flags: [],
  eo_doc: null,
  professional_liability_doc: null,
  // ── Profile-ready additions ─────────────────────────────────────────
  photo: null,            // applicant headshot UploadRef
  firm_tagline: "",
  years_founded: "",
  team_size: "",
  bio: "",
  lifestyle_focuses: [],
  credentials: [],        // [{abbr, year, name}]
  standard_credentials: [],
};

/**
 * /register/architect — three-step application form, plus a review screen.
 *
 * Props:
 *   mode: "submit" | "resubmit"     (default "submit")
 *   initial: ApplicationFormFields  (when resubmitting, prefilled with the
 *                                    existing application's data)
 *   applicationId, actorUserId      (when resubmitting)
 *   reviewNote                      (the admin note to display at top in
 *                                    resubmit mode)
 */
export default function RegisterArchitect({
  mode = "submit",
  initial = null,
  applicationId,
  actorUserId,
  reviewNote,
}) {
  const navigate = useNavigate();
  const { setSessionDirect } = useRole();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(() => prefillForm(initial));
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  function setContact(field, value) {
    setForm((f) => ({ ...f, contact: { ...f.contact, [field]: value } }));
  }

  function toggleArr(field, value) {
    setForm((f) => {
      const arr = f[field] || [];
      const next = arr.includes(value)
        ? arr.filter((v) => v !== value)
        : [...arr, value];
      return { ...f, [field]: next };
    });
  }

  function validateStep(s) {
    const e = {};
    if (s === 1) {
      if (!form.contact.name.trim()) e.name = "Full name is required.";
      if (!form.firm.trim()) e.firm = "Firm name is required.";
      if (!form.contact.email.trim()) e.email = "Email is required.";
      else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.contact.email))
        e.email = "Enter a valid email address.";
      if (!form.license_no.trim())
        e.license_no = "Indiana architectural license number is required.";
      if (form.license_no.trim() && !form.license_verified)
        e.license_verified =
          "Please click 'Verify with IPLA' to confirm your license number.";
      // Postal address validation — every field required, ZIP must be 5
      // or 9 digits, state must be a 2-letter code.
      const pa = form.postal_address || {};
      if (!pa.street?.trim()) e.postal_street = "Street address is required.";
      if (!pa.city?.trim()) e.postal_city = "City is required.";
      const stateClean = (pa.state || "").trim();
      if (!stateClean || stateClean.length !== 2)
        e.postal_state = "Use a 2-letter state code (e.g. IN).";
      const zipClean = (pa.postal_code || "").replace(/-/g, "");
      if (!/^\d{5}(\d{4})?$/.test(zipClean))
        e.postal_code = "ZIP must be 5 or 9 digits.";
      if (form.firm_tagline && form.firm_tagline.length > TAGLINE_MAX) {
        e.firm_tagline = `Tagline must be ${TAGLINE_MAX} characters or fewer.`;
      }
      if (form.years_founded) {
        const y = Number(form.years_founded);
        const cy = new Date().getFullYear();
        if (Number.isNaN(y) || y < 1800 || y > cy) {
          e.years_founded = `Year founded must be between 1800 and ${cy}.`;
        }
      }
      if (form.team_size) {
        const t = Number(form.team_size);
        if (Number.isNaN(t) || t < 1 || t > 500) {
          e.team_size = "Team size must be between 1 and 500.";
        }
      }
    } else if (s === 2) {
      if (form.service_areas.length === 0)
        e.service_areas = "Select at least one service area.";
      if (form.specialisations.length === 0)
        e.specialisations = "Select at least one typology specialisation.";
      if (form.practice_statement.trim().length < PRACTICE_MIN)
        e.practice_statement = `Practice statement must be at least ${PRACTICE_MIN} characters.`;
      if (form.bio && form.bio.length > BIO_MAX) {
        e.bio = `Biography must be ${BIO_MAX} characters or fewer.`;
      }
      if ((form.lifestyle_focuses || []).length > LIFESTYLE_MAX) {
        e.lifestyle_focuses = `Up to ${LIFESTYLE_MAX} focuses are supported.`;
      }
    } else if (s === 3) {
      if (!form.eo_doc) e.eo_doc = "E&O insurance certificate is required.";
      if (!form.professional_liability_doc)
        e.professional_liability_doc =
          "Professional Liability insurance certificate is required.";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function next() {
    if (validateStep(step)) setStep((s) => Math.min(4, s + 1));
  }
  function prev() {
    setStep((s) => Math.max(1, s - 1));
  }

  async function submit() {
    if (!validateStep(1) || !validateStep(2) || !validateStep(3)) {
      toast.error("Please fix the highlighted fields before submitting.");
      return;
    }
    setSubmitting(true);
    setSubmitError("");
    try {
      const payload = {
        ...form,
        years_practising:
          form.years_practising === "" || form.years_practising == null
            ? null
            : Number(form.years_practising),
        years_founded:
          form.years_founded === "" || form.years_founded == null
            ? null
            : Number(form.years_founded),
        team_size:
          form.team_size === "" || form.team_size == null
            ? null
            : Number(form.team_size),
        // Clean credentials: drop empty rows, coerce year to int if present.
        credentials: (form.credentials || [])
          .filter((c) => (c.abbr || c.name || c.year))
          .map((c) => ({
            abbr: (c.abbr || "").trim(),
            year:
              c.year === "" || c.year == null
                ? null
                : Number(c.year),
            name: (c.name || "").trim(),
          })),
      };
      if (mode === "resubmit") {
        await resubmitApplication(applicationId, {
          ...payload,
          actor_user_id: actorUserId,
        });
        toast.success("Application resubmitted.");
        navigate("/architect/application");
      } else {
        const { user } = await submitApplication(payload);
        setSessionDirect("architect", user);
        toast.success("Application submitted. Welcome.");
        navigate("/architect/application");
      }
    } catch (e) {
      const detail =
        e?.response?.data?.detail ||
        e?.message ||
        "Could not submit. Please try again.";
      setSubmitError(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="wn-page min-h-screen" data-testid="register-architect-page">
      <Toaster position="top-right" richColors closeButton />
      {/* Slim indigo top bar with brand only — same as Sign-in */}
      <div style={{ backgroundColor: "#1B1F4B" }}>
        <div className="max-w-[1280px] mx-auto px-6 h-16 flex items-center">
          <Link
            to="/"
            data-testid="register-brand"
            style={{
              color: "#FBFAFD",
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 600,
              fontSize: 18,
            }}
          >
            WHOLE Neighborhoods
          </Link>
        </div>
      </div>

      <div className="max-w-[860px] mx-auto px-6 pt-14 pb-16">
        <ProgressDots step={step} />

        {mode === "resubmit" && reviewNote && step < 4 && (
          <ReviewNotePanel note={reviewNote} />
        )}

        {step === 1 && (
          <Step1
            form={form}
            errors={errors}
            setContact={setContact}
            set={set}
          />
        )}
        {step === 2 && (
          <Step2
            form={form}
            errors={errors}
            set={set}
            toggleArr={toggleArr}
          />
        )}
        {step === 3 && (
          <Step3 form={form} errors={errors} set={set} />
        )}
        {step === 4 && (
          <Review form={form} onEdit={(s) => setStep(s)} />
        )}

        {submitError && (
          <div
            data-testid="submit-error"
            className="mt-6 wn-card"
            style={{
              borderColor: "#A23B2E",
              backgroundColor: "rgba(162,59,46,0.06)",
              padding: "12px 16px",
              color: "#A23B2E",
              display: "flex",
              alignItems: "center",
              gap: 10,
              fontSize: 13,
            }}
          >
            <AlertCircle size={16} />
            {submitError}
          </div>
        )}

        <div className="mt-10 flex items-center justify-between">
          <button
            type="button"
            className="wn-btn wn-btn-tertiary"
            onClick={() => (step === 1 ? navigate("/") : prev())}
            data-testid="register-back-btn"
          >
            <ArrowLeft size={16} /> {step === 1 ? "Cancel" : "Back"}
          </button>
          {step < 4 ? (
            <button
              type="button"
              className="wn-btn wn-btn-primary"
              onClick={next}
              data-testid="register-next-btn"
            >
              Next <ArrowRight size={16} />
            </button>
          ) : (
            <button
              type="button"
              className="wn-btn wn-btn-accent"
              onClick={submit}
              disabled={submitting}
              data-testid="register-submit-btn"
            >
              {submitting
                ? "Submitting…"
                : mode === "resubmit"
                ? "Resubmit application"
                : "Submit application"}
              <ArrowRight size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function prefillForm(initial) {
  if (!initial) return EMPTY_FORM;
  return {
    contact: {
      name: initial.contact?.name || "",
      email: initial.contact?.email || "",
      phone: initial.contact?.phone || "",
    },
    firm: initial.firm || "",
    license_no: initial.license_no || "",
    license_verified: !!initial.license_verified,
    postal_address: {
      street: initial.postal_address?.street || "",
      city: initial.postal_address?.city || "",
      state: initial.postal_address?.state || "IN",
      postal_code: initial.postal_address?.postal_code || "",
    },
    years_practising:
      initial.years_practising == null ? "" : String(initial.years_practising),
    service_areas: initial.service_areas || [],
    specialisations: initial.specialisations || [],
    practice_statement: initial.practice_statement || "",
    demographic_flags: initial.demographic_flags || [],
    eo_doc: initial.documents?.eo || null,
    professional_liability_doc:
      initial.documents?.professional_liability || null,
    photo: initial.photo || null,
    firm_tagline: initial.firm_tagline || "",
    years_founded:
      initial.years_founded == null ? "" : String(initial.years_founded),
    team_size: initial.team_size == null ? "" : String(initial.team_size),
    bio: initial.bio || "",
    lifestyle_focuses: initial.lifestyle_focuses || [],
    credentials: (initial.credentials || []).map((c) => ({
      abbr: c.abbr || "",
      year: c.year == null ? "" : String(c.year),
      name: c.name || "",
    })),
    standard_credentials: initial.standard_credentials || [],
  };
}

// ---------------------------------------------------------------------------
// Progress dots
// ---------------------------------------------------------------------------
function ProgressDots({ step }) {
  const items = [1, 2, 3];
  return (
    <div
      className="flex items-center gap-2 mb-8"
      data-testid="register-progress"
    >
      {items.map((n, i) => {
        const isDone = step > n;
        const isCurrent = step === n || (n === 3 && step === 4);
        const bg = isCurrent ? "#1B1F4B" : isDone ? "#5B6196" : "#FBFAFD";
        const color = isCurrent || isDone ? "#FBFAFD" : "#9AA0C0";
        return (
          <React.Fragment key={n}>
            <div
              data-testid={`progress-dot-${n}`}
              style={{
                width: 28,
                height: 28,
                borderRadius: 999,
                backgroundColor: bg,
                color,
                border: `1px solid ${
                  isCurrent ? "#1B1F4B" : isDone ? "#5B6196" : "#D9D8E4"
                }`,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: '"Nunito Sans", sans-serif',
                fontWeight: 700,
                fontSize: 13,
              }}
            >
              {isDone ? <Check size={14} /> : n}
            </div>
            {i < items.length - 1 && (
              <div
                style={{
                  height: 1,
                  flex: 1,
                  backgroundColor: step > n ? "#5B6196" : "#D9D8E4",
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function StepHeader({ eyebrow, title, blurb }) {
  return (
    <div className="mb-7">
      <div className="wn-label" style={{ color: "#8F3C23" }}>
        {eyebrow}
      </div>
      <h1
        className="mt-2"
        style={{ fontSize: 32, fontWeight: 600, lineHeight: 1.1 }}
      >
        {title}
      </h1>
      {blurb && (
        <p
          className="mt-3"
          style={{ color: "#6C6B85", fontSize: 15, lineHeight: 1.55 }}
        >
          {blurb}
        </p>
      )}
    </div>
  );
}

function Field({ label, error, children, testid, hint }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="wn-label" style={{ display: "block", marginBottom: 6 }}>
        {label}
      </span>
      {children}
      {hint && !error && (
        <span
          style={{
            display: "block",
            marginTop: 6,
            color: "#6C6B85",
            fontSize: 11.5,
            lineHeight: 1.5,
            fontStyle: "italic",
            fontFamily: '"Newsreader", Georgia, serif',
          }}
          data-testid={testid ? `${testid}-hint` : undefined}
        >
          {hint}
        </span>
      )}
      {error && (
        <span
          style={{
            display: "block",
            marginTop: 6,
            color: "#A23B2E",
            fontSize: 12,
            fontWeight: 600,
          }}
          data-testid={`${testid}-error`}
        >
          {error}
        </span>
      )}
    </label>
  );
}

// ---------------------------------------------------------------------------
// Step 1
// ---------------------------------------------------------------------------
function Step1({ form, errors, setContact, set }) {
  return (
    <div data-testid="register-step-1">
      <StepHeader
        eyebrow="Step 1 of 3 — About you & your firm"
        title="Tell us about you and your practice."
        blurb="The basics we need to verify your license, reach you with questions, and publish a credible day-one profile when you're approved."
      />
      <div className="grid gap-5">
        <Field label="Full name *" error={errors.name} testid="field-name">
          <input
            className="wn-input"
            value={form.contact.name}
            onChange={(e) => setContact("name", e.target.value)}
            data-testid="input-name"
          />
        </Field>
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Firm name *" error={errors.firm} testid="field-firm">
            <input
              className="wn-input"
              value={form.firm}
              onChange={(e) => set("firm", e.target.value)}
              data-testid="input-firm"
            />
          </Field>
          <Field
            label="Indiana architectural license number *"
            error={errors.license_no || errors.license_verified}
            testid="field-license"
            hint="As issued by the Indiana Professional Licensing Agency."
          >
            <LicenseVerifyControl
              value={form.license_no}
              verified={form.license_verified}
              onChange={(v) => {
                // Editing the number invalidates a prior verification.
                set("license_no", v);
                if (form.license_verified) set("license_verified", false);
              }}
              onVerify={() => set("license_verified", true)}
              onClearVerification={() => set("license_verified", false)}
            />
          </Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Email *" error={errors.email} testid="field-email">
            <input
              className="wn-input"
              value={form.contact.email}
              onChange={(e) => setContact("email", e.target.value)}
              data-testid="input-email"
            />
          </Field>
          <Field label="Phone" testid="field-phone">
            <input
              className="wn-input"
              value={form.contact.phone}
              onChange={(e) => setContact("phone", e.target.value)}
              data-testid="input-phone"
            />
          </Field>
        </div>

        {/* ── Postal address ─────────────────────────────────────────
            Required for state-board license verification and physical
            correspondence. State defaults to IN (Indiana) but accepts
            any 2-letter US state code. */}
        <Field
          label="Office postal address *"
          error={
            errors.postal_street ||
            errors.postal_city ||
            errors.postal_state ||
            errors.postal_code
          }
          testid="field-postal-address"
          hint="Used by the state board to verify your license. Not shown publicly."
        >
          <div className="grid gap-3">
            <input
              className="wn-input"
              placeholder="Street address (e.g. 123 N Pennsylvania St, Suite 400)"
              value={form.postal_address.street}
              onChange={(e) =>
                set("postal_address", {
                  ...form.postal_address,
                  street: e.target.value,
                })
              }
              data-testid="input-postal-street"
            />
            <div
              className="grid gap-3"
              style={{ gridTemplateColumns: "1fr 100px 140px" }}
            >
              <input
                className="wn-input"
                placeholder="City"
                value={form.postal_address.city}
                onChange={(e) =>
                  set("postal_address", {
                    ...form.postal_address,
                    city: e.target.value,
                  })
                }
                data-testid="input-postal-city"
              />
              <input
                className="wn-input"
                placeholder="State"
                maxLength={2}
                value={form.postal_address.state}
                onChange={(e) =>
                  set("postal_address", {
                    ...form.postal_address,
                    state: e.target.value.toUpperCase(),
                  })
                }
                data-testid="input-postal-state"
              />
              <input
                className="wn-input tabular-nums"
                placeholder="ZIP"
                inputMode="numeric"
                value={form.postal_address.postal_code}
                onChange={(e) =>
                  set("postal_address", {
                    ...form.postal_address,
                    postal_code: e.target.value
                      .replace(/[^\d-]/g, "")
                      .slice(0, 10),
                  })
                }
                data-testid="input-postal-zip"
              />
            </div>
          </div>
        </Field>
        <div className="grid sm:grid-cols-3 gap-5">
          <Field label="Years practising" testid="field-years">
            <input
              className="wn-input tabular-nums"
              inputMode="numeric"
              value={form.years_practising}
              onChange={(e) =>
                set(
                  "years_practising",
                  e.target.value.replace(/[^\d]/g, "").slice(0, 2),
                )
              }
              data-testid="input-years"
            />
          </Field>
          <Field
            label="Year firm founded"
            error={errors.years_founded}
            testid="field-years-founded"
          >
            <input
              className="wn-input tabular-nums"
              inputMode="numeric"
              placeholder="e.g. 2012"
              value={form.years_founded}
              onChange={(e) =>
                set(
                  "years_founded",
                  e.target.value.replace(/[^\d]/g, "").slice(0, 4),
                )
              }
              data-testid="input-years-founded"
            />
          </Field>
          <Field
            label="Team size"
            error={errors.team_size}
            testid="field-team-size"
          >
            <input
              className="wn-input tabular-nums"
              inputMode="numeric"
              placeholder="e.g. 6"
              value={form.team_size}
              onChange={(e) =>
                set(
                  "team_size",
                  e.target.value.replace(/[^\d]/g, "").slice(0, 3),
                )
              }
              data-testid="input-team-size"
            />
          </Field>
        </div>
        <Field
          label="Firm tagline"
          error={errors.firm_tagline}
          testid="field-firm-tagline"
        >
          <input
            className="wn-input"
            placeholder="A short sentence describing your stance (5–10 words)."
            value={form.firm_tagline}
            maxLength={TAGLINE_MAX}
            onChange={(e) => set("firm_tagline", e.target.value)}
            data-testid="input-firm-tagline"
          />
          <div
            className="tabular-nums"
            style={{ color: "#6C6B85", fontSize: 11, marginTop: 4 }}
          >
            {(form.firm_tagline || "").length}/{TAGLINE_MAX} characters.
            Appears beneath your firm name on the public profile.
          </div>
        </Field>
        <Field
          label="Headshot photograph (optional)"
          error={errors.photo}
          testid="field-photo"
        >
          <PhotoApplicationUpload
            value={form.photo}
            onChange={(v) => set("photo", v)}
          />
        </Field>
      </div>
    </div>
  );
}

function LicenseVerifyControl({
  value,
  verified,
  onChange,
  onVerify,
  onClearVerification,
}) {
  // Simulated license-verification flow. In production this would call
  // the Indiana Professional Licensing Agency's public e-Verification
  // endpoint; for the PoC we run a 1.2s spinner then accept whatever
  // number the applicant typed. The visible "Verified" badge persists
  // until the applicant edits the license number.
  const [busy, setBusy] = React.useState(false);
  const canVerify = value.trim().length >= 3 && !verified && !busy;
  const handle = () => {
    if (!canVerify) return;
    setBusy(true);
    setTimeout(() => {
      setBusy(false);
      onVerify();
    }, 1200);
  };
  return (
    <div data-testid="license-verify-block">
      <div className="flex items-stretch gap-2">
        <input
          className="wn-input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="e.g. AR-12345"
          data-testid="input-license"
          style={{ flex: 1 }}
        />
        {verified ? (
          <button
            type="button"
            onClick={onClearVerification}
            className="wn-btn wn-btn-tertiary inline-flex items-center"
            style={{ gap: 6, fontSize: 12, whiteSpace: "nowrap" }}
            data-testid="license-clear-verification"
            title="Re-enter a different license number"
          >
            <X size={13} /> Clear
          </button>
        ) : (
          <button
            type="button"
            onClick={handle}
            disabled={!canVerify}
            className="wn-btn wn-btn-primary inline-flex items-center"
            style={{
              gap: 6,
              fontSize: 12,
              whiteSpace: "nowrap",
              opacity: canVerify ? 1 : 0.55,
              cursor: canVerify ? "pointer" : "not-allowed",
            }}
            data-testid="license-verify-btn"
          >
            <ShieldCheck size={13} />
            {busy ? "Verifying…" : "Verify with IPLA"}
          </button>
        )}
      </div>
      {verified && (
        <div
          className="flex items-center gap-2 mt-2"
          style={{
            padding: "8px 12px",
            background: "rgba(79,97,73,0.08)",
            border: "1px solid rgba(79,97,73,0.32)",
            borderRadius: 4,
            color: "#4F6149",
            fontSize: 12.5,
            fontWeight: 600,
          }}
          data-testid="license-verified-badge"
        >
          <ShieldCheck size={14} />
          Verified against the Indiana Professional Licensing Agency.
        </div>
      )}
      <a
        href={IPLA_LOOKUP_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center mt-2"
        style={{
          color: "#1B1F4B",
          fontSize: 11.5,
          textDecoration: "none",
          gap: 4,
          fontWeight: 700,
          letterSpacing: "0.03em",
        }}
        data-testid="license-ipla-link"
      >
        Open the IPLA lookup <ExternalLink size={11} />
      </a>
    </div>
  );
}

function StandardCredentialsChecklist({ value, onChange }) {
  const set = new Set(value || []);
  const toggle = (key) => {
    const next = new Set(set);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange([...next]);
  };
  return (
    <div
      className="grid gap-2"
      data-testid="standard-credentials-checklist"
    >
      {STANDARD_CREDENTIALS.map((c) => {
        const on = set.has(c.key);
        return (
          <label
            key={c.key}
            className="flex items-start gap-3"
            style={{
              padding: "10px 14px",
              border: `1px solid ${on ? "#B14E2F" : "#E8E7EE"}`,
              borderRadius: 4,
              background: on ? "rgba(177,78,47,0.06)" : "#FBFAFD",
              cursor: "pointer",
              transition: "background-color 140ms ease, border-color 140ms ease",
            }}
            data-testid={`standard-credential-${c.key}`}
          >
            <input
              type="checkbox"
              checked={on}
              onChange={() => toggle(c.key)}
              data-testid={`standard-credential-${c.key}-input`}
              style={{ marginTop: 3, accentColor: "#B14E2F" }}
            />
            <span>
              <span
                style={{
                  fontWeight: 700,
                  fontSize: 13.5,
                  color: "#1B1F4B",
                  display: "block",
                }}
              >
                {c.label}
              </span>
              <span style={{ color: "#6C6B85", fontSize: 12, lineHeight: 1.5 }}>
                {c.desc}
              </span>
            </span>
          </label>
        );
      })}
    </div>
  );
}

function PhotoApplicationUpload({ value, onChange }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  async function onPick(file) {
    if (!file) return;
    setBusy(true);
    setErr("");
    try {
      const meta = await uploadFile(file, "applicant_photo");
      onChange({
        id: meta.id,
        label: "Headshot",
        original_name: meta.original_name,
        mime: meta.mime,
        size: meta.size,
        url: meta.url,
      });
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Upload failed.";
      setErr(typeof msg === "string" ? msg : "Upload failed.");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }
  return (
    <div className="flex items-center gap-4" data-testid="photo-upload-block">
      <div
        style={{
          width: 80,
          height: 80,
          borderRadius: 999,
          border: "1px solid #D9D8E4",
          background: value?.url
            ? `url(${value.url}) center/cover no-repeat`
            : "#FBFAFD",
          flexShrink: 0,
        }}
        aria-hidden
        data-testid="photo-upload-preview"
      />
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="wn-btn wn-btn-tertiary"
          data-testid="photo-upload-trigger"
        >
          <Upload size={14} />
          {busy ? "Uploading…" : value ? "Replace photo" : "Upload photo"}
        </button>
        {value && (
          <button
            type="button"
            onClick={() => onChange(null)}
            className="wn-btn wn-btn-tertiary"
            data-testid="photo-upload-remove"
          >
            <X size={14} /> Remove
          </button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png"
          hidden
          onChange={(e) => onPick(e.target.files?.[0])}
        />
      </div>
      {err && (
        <span
          style={{ color: "#A23B2E", fontSize: 12, fontWeight: 600 }}
          data-testid="photo-upload-error"
        >
          {err}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 2
// ---------------------------------------------------------------------------
function Step2({ form, errors, set, toggleArr }) {
  const remaining = Math.max(0, PRACTICE_MIN - form.practice_statement.length);
  return (
    <div data-testid="register-step-2">
      <StepHeader
        eyebrow="Step 2 of 3 — Practice details"
        title="Where you work, what you build."
        blurb="These guide where your designs surface and which projects we connect you with."
      />
      <Field
        label="Service areas *"
        error={errors.service_areas}
        testid="field-service-areas"
      >
        <ChipRow
          options={SERVICE_AREAS.map((s) => ({ key: s, label: s }))}
          selected={form.service_areas}
          onToggle={(v) => toggleArr("service_areas", v)}
          testidPrefix="chip-area"
        />
      </Field>
      <div className="mt-6">
        <Field
          label="Typology specialisations *"
          error={errors.specialisations}
          testid="field-specialisations"
        >
          <ChipRow
            options={TYPOLOGIES}
            selected={form.specialisations}
            onToggle={(v) => toggleArr("specialisations", v)}
            testidPrefix="chip-typology"
          />
        </Field>
      </div>
      <div className="mt-6">
        <Field
          label="Practice statement / biography *"
          error={errors.practice_statement}
          testid="field-statement"
        >
          <textarea
            className="wn-input"
            style={{ minHeight: 140, padding: "12px", lineHeight: 1.55 }}
            value={form.practice_statement}
            onChange={(e) => set("practice_statement", e.target.value)}
            data-testid="input-statement"
          />
          <div
            className="mt-1 flex items-center justify-between tabular-nums"
            style={{ fontSize: 12, color: "#6C6B85" }}
          >
            <span>
              At least {PRACTICE_MIN} characters. Plain language is best.
            </span>
            <span data-testid="statement-counter">
              {form.practice_statement.length}
              {remaining > 0 ? ` · ${remaining} to go` : " ✓"}
            </span>
          </div>
        </Field>
      </div>
      <div className="mt-6">
        <Field
          label="Short biography (optional)"
          error={errors.bio}
          testid="field-bio"
        >
          <textarea
            className="wn-input"
            style={{ minHeight: 110, padding: "12px", lineHeight: 1.55 }}
            placeholder="Personal voice — your training, what drives you, how you collaborate with clients."
            value={form.bio}
            maxLength={BIO_MAX}
            onChange={(e) => set("bio", e.target.value)}
            data-testid="input-bio"
          />
          <div
            className="tabular-nums"
            style={{ color: "#6C6B85", fontSize: 11, marginTop: 4 }}
          >
            {(form.bio || "").length}/{BIO_MAX} characters. Shown beneath
            your practice statement on the public profile.
          </div>
        </Field>
      </div>
      <div className="mt-6">
        <Field
          label="Who you work for (optional)"
          error={errors.lifestyle_focuses}
          testid="field-lifestyle"
        >
          <p style={{ color: "#6C6B85", fontSize: 12, marginBottom: 8, lineHeight: 1.5 }}>
            Up to {LIFESTYLE_MAX} short phrases describing the households or
            clients you build for. Example: <em>Aging-in-place residents</em>,
            <em> First-time homebuyers</em>, <em>CDCs</em>.
          </p>
          <TagInline
            tags={form.lifestyle_focuses}
            onChange={(arr) => set("lifestyle_focuses", arr)}
            placeholder="Add a focus and press Enter…"
            max={LIFESTYLE_MAX}
            testid="lifestyle"
          />
        </Field>
      </div>
      <div className="mt-6">
        <Field
          label="Demographic flags (optional, self-attested)"
          testid="field-flags"
        >
          <ChipRow
            options={FLAGS.map((f) => ({ key: f, label: f }))}
            selected={form.demographic_flags}
            onToggle={(v) => toggleArr("demographic_flags", v)}
            testidPrefix="chip-flag"
          />
          <div
            className="mt-2"
            style={{ fontSize: 12, color: "#6C6B85", fontStyle: "italic" }}
          >
            These are self-attested; documentation may be requested during review.
          </div>
        </Field>
      </div>
    </div>
  );
}

function ChipRow({ options, selected, onToggle, testidPrefix }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const isOn = selected.includes(opt.key);
        return (
          <button
            type="button"
            key={opt.key}
            onClick={() => onToggle(opt.key)}
            data-testid={`${testidPrefix}-${opt.key}`}
            style={{
              padding: "7px 14px",
              borderRadius: 999,
              border: `1px solid ${isOn ? "#1B1F4B" : "#D9D8E4"}`,
              backgroundColor: isOn ? "#1B1F4B" : "#FBFAFD",
              color: isOn ? "#FBFAFD" : "#272636",
              fontFamily: '"Nunito Sans", sans-serif',
              fontWeight: 600,
              fontSize: 12,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              cursor: "pointer",
              transition: "background-color 160ms ease, color 160ms ease, border-color 160ms ease",
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 3 — credentials + uploads
// ---------------------------------------------------------------------------
function Step3({ form, errors, set }) {
  return (
    <div data-testid="register-step-3">
      <StepHeader
        eyebrow="Step 3 of 3 — Credentials & insurance"
        title="Credentials, insurance, and registration."
        blurb="Tick any green-building credentials, list custom certifications, attach both insurance certificates, and review the one-time setup fee."
      />

      <div data-testid="standard-credentials-section">
        <div className="wn-label">Green-building credentials</div>
        <p style={{ color: "#6C6B85", fontSize: 12, marginTop: 4, marginBottom: 10, lineHeight: 1.5 }}>
          Tick every credential you currently hold. Verification is part
          of the registration fee.
        </p>
        <StandardCredentialsChecklist
          value={form.standard_credentials}
          onChange={(v) => set("standard_credentials", v)}
        />
      </div>

      <div className="mt-7" data-testid="credentials-section">
        <div className="wn-label">Other credentials (optional)</div>
        <p style={{ color: "#6C6B85", fontSize: 12, marginTop: 4, marginBottom: 8, lineHeight: 1.5 }}>
          Up to {CREDENTIALS_MAX} entries — e.g. AIA membership, NCARB
          certification. Each surfaces in the Credentials rail on your
          public profile.
        </p>
        <CredentialsInline
          rows={form.credentials}
          onChange={(arr) => set("credentials", arr)}
        />
      </div>

      <div className="mt-7">
        <div className="wn-label" style={{ marginBottom: 10 }}>
          Insurance certificates *
        </div>
        <p
          style={{
            color: "#6C6B85",
            fontSize: 12,
            marginTop: 0,
            marginBottom: 12,
            lineHeight: 1.5,
          }}
        >
          Both required. PDF, JPG, or PNG up to 10&nbsp;MB each.
        </p>
        <div className="grid gap-4 md:grid-cols-2">
          <FileUploadBlock
            label="E&O (Errors & Omissions) insurance"
            purpose="eo"
            value={form.eo_doc}
            onChange={(v) => set("eo_doc", v)}
            testid="upload-eo"
            error={errors.eo_doc}
          />
          <FileUploadBlock
            label="Professional Liability insurance"
            purpose="professional_liability"
            value={form.professional_liability_doc}
            onChange={(v) => set("professional_liability_doc", v)}
            testid="upload-prof-liability"
            error={errors.professional_liability_doc}
          />
        </div>
      </div>

      <RegistrationFeeNotice />
    </div>
  );
}

function RegistrationFeeNotice() {
  return (
    <div
      className="mt-8"
      style={{
        padding: "18px 22px",
        background: "#1B1F4B",
        color: "#FBFAFD",
        borderRadius: 4,
        display: "grid",
        gridTemplateColumns: "1fr auto",
        alignItems: "center",
        gap: 18,
      }}
      data-testid="registration-fee-notice"
    >
      <div>
        <div
          className="wn-label"
          style={{ color: "#E8A07F", marginBottom: 6 }}
        >
          One-time registration fee
        </div>
        <p
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            fontSize: 14,
            lineHeight: 1.55,
            margin: 0,
            color: "rgba(251,250,253,0.85)",
          }}
        >
          Covers account setup, IPLA license re-check, and verification
          of your insurance certificates. Invoiced after your application
          is approved — no card required at submission.
        </p>
      </div>
      <div
        className="tabular-nums"
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontSize: 34,
          fontWeight: 600,
          lineHeight: 1,
        }}
        data-testid="registration-fee-amount"
      >
        ${REGISTRATION_FEE_USD}
      </div>
    </div>
  );
}

function CredentialsInline({ rows, onChange }) {
  const list = rows && rows.length ? rows : [];
  function update(i, key, value) {
    const next = list.map((r, idx) => (idx === i ? { ...r, [key]: value } : r));
    onChange(next);
  }
  function remove(i) {
    onChange(list.filter((_, idx) => idx !== i));
  }
  function add() {
    if (list.length >= CREDENTIALS_MAX) return;
    onChange([...list, { abbr: "", year: "", name: "" }]);
  }
  return (
    <div data-testid="credentials-inline" className="space-y-2">
      {list.map((row, i) => (
        <div
          key={i}
          className="grid gap-2"
          style={{ gridTemplateColumns: "120px 100px 1fr 40px" }}
          data-testid={`credentials-row-${i}`}
        >
          <input
            className="wn-input"
            placeholder="AIA"
            value={row.abbr || ""}
            maxLength={24}
            onChange={(e) => update(i, "abbr", e.target.value)}
            data-testid={`credentials-row-${i}-abbr`}
          />
          <input
            className="wn-input tabular-nums"
            inputMode="numeric"
            placeholder="Year"
            value={row.year || ""}
            onChange={(e) =>
              update(i, "year", e.target.value.replace(/[^\d]/g, "").slice(0, 4))
            }
            data-testid={`credentials-row-${i}-year`}
          />
          <input
            className="wn-input"
            placeholder="Full credential name"
            value={row.name || ""}
            maxLength={200}
            onChange={(e) => update(i, "name", e.target.value)}
            data-testid={`credentials-row-${i}-name`}
          />
          <button
            type="button"
            onClick={() => remove(i)}
            className="wn-btn wn-btn-tertiary"
            style={{ padding: 0, justifyContent: "center" }}
            aria-label="Remove credential"
            data-testid={`credentials-row-${i}-remove`}
          >
            <X size={14} />
          </button>
        </div>
      ))}
      {list.length < CREDENTIALS_MAX && (
        <button
          type="button"
          onClick={add}
          className="wn-btn wn-btn-tertiary mt-1"
          style={{ paddingLeft: 0 }}
          data-testid="credentials-add"
        >
          + Add credential ({list.length}/{CREDENTIALS_MAX})
        </button>
      )}
    </div>
  );
}

function TagInline({ tags, onChange, placeholder, max, testid }) {
  const [draft, setDraft] = useState("");
  function commit() {
    const v = draft.trim();
    if (!v) return;
    if (tags.length >= max) {
      setDraft("");
      return;
    }
    if (tags.includes(v)) {
      setDraft("");
      return;
    }
    onChange([...tags, v]);
    setDraft("");
  }
  return (
    <div data-testid={`${testid}-inline`}>
      <div className="flex flex-wrap gap-2 mb-2">
        {tags.map((t, i) => (
          <span
            key={i}
            className="inline-flex items-center"
            style={{
              padding: "5px 10px",
              borderRadius: 999,
              border: "1px solid #1B1F4B",
              background: "#1B1F4B",
              color: "#FBFAFD",
              fontSize: 12,
              fontWeight: 600,
              gap: 6,
            }}
            data-testid={`${testid}-tag-${i}`}
          >
            {t}
            <button
              type="button"
              onClick={() => onChange(tags.filter((_, idx) => idx !== i))}
              style={{
                background: "transparent",
                border: "none",
                color: "#FBFAFD",
                cursor: "pointer",
                display: "inline-flex",
                padding: 0,
              }}
              aria-label={`Remove ${t}`}
              data-testid={`${testid}-tag-${i}-remove`}
            >
              <X size={12} />
            </button>
          </span>
        ))}
      </div>
      {tags.length < max && (
        <input
          className="wn-input"
          placeholder={placeholder}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit();
            }
          }}
          onBlur={commit}
          data-testid={`${testid}-input`}
        />
      )}
      <div
        className="tabular-nums"
        style={{ color: "#6C6B85", fontSize: 11, marginTop: 4 }}
      >
        {tags.length}/{max} added.
      </div>
    </div>
  );
}

function FileUploadBlock({ label, purpose, value, onChange, testid, error }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState("");

  async function onPick(file) {
    if (!file) return;
    setBusy(true);
    setLocalError("");
    try {
      const meta = await uploadFile(file, purpose);
      onChange({
        id: meta.id,
        label,
        original_name: meta.original_name,
        mime: meta.mime,
        size: meta.size,
        url: meta.url,
      });
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Upload failed.";
      setLocalError(typeof msg === "string" ? msg : "Upload failed.");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  return (
    <div data-testid={testid}>
      <div className="wn-label" style={{ marginBottom: 6 }}>
        {label}
      </div>
      {value ? (
        <UploadedRow
          file={value}
          onRemove={() => onChange(null)}
          testidPrefix={testid}
        />
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid={`${testid}-trigger`}
          style={{
            width: "100%",
            backgroundColor: "#FBFAFD",
            border: "1px dashed #9AA0C0",
            borderRadius: 10,
            padding: "22px 18px",
            display: "flex",
            alignItems: "center",
            gap: 14,
            cursor: busy ? "wait" : "pointer",
            color: "#1B1F4B",
            textAlign: "left",
          }}
        >
          <Upload size={20} />
          <div>
            <div style={{ fontWeight: 700, fontSize: 14 }}>
              {busy ? "Uploading…" : "Click to choose a file"}
            </div>
            <div style={{ fontSize: 12, color: "#6C6B85", marginTop: 2 }}>
              PDF, JPG, or PNG · up to 10 MB.
            </div>
          </div>
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,image/jpeg,image/png"
        hidden
        onChange={(e) => onPick(e.target.files?.[0])}
      />
      {(localError || error) && (
        <div
          style={{
            marginTop: 6,
            color: "#A23B2E",
            fontSize: 12,
            fontWeight: 600,
          }}
          data-testid={`${testid}-error`}
        >
          {localError || error}
        </div>
      )}
    </div>
  );
}

export function UploadedRow({ file, onRemove, testidPrefix }) {
  const isImage = (file.mime || "").startsWith("image/");
  const Icon = isImage ? ImageIcon : FileText;
  return (
    <div
      className="wn-card"
      style={{
        padding: "12px 14px",
        display: "flex",
        alignItems: "center",
        gap: 12,
      }}
      data-testid={`${testidPrefix}-row`}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          backgroundColor: "rgba(91,97,150,0.12)",
          color: "#1B1F4B",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon size={16} />
      </div>
      <div className="min-w-0 flex-1">
        <div
          style={{
            fontWeight: 700,
            fontSize: 13,
            color: "#1B1F4B",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {file.original_name || file.label}
        </div>
        <div
          className="tabular-nums"
          style={{ fontSize: 11, color: "#6C6B85", marginTop: 2 }}
        >
          {humanSize(file.size)}
          {file.mime ? ` · ${file.mime}` : ""}
        </div>
      </div>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="wn-btn wn-btn-tertiary"
          style={{ height: 32, padding: "0 8px" }}
          data-testid={`${testidPrefix}-remove`}
          aria-label="Remove file"
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
}

function humanSize(bytes) {
  if (bytes == null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

// ---------------------------------------------------------------------------
// Review
// ---------------------------------------------------------------------------
function Review({ form, onEdit }) {
  return (
    <div data-testid="register-review">
      <StepHeader
        eyebrow="Review — final check"
        title="Does this look right?"
        blurb="Confirm your information below, then submit. You'll see your application status next."
      />
      <ReviewSection
        title="About you"
        step={1}
        onEdit={onEdit}
        rows={[
          ["Full name", form.contact.name],
          ["Firm", form.firm],
          ["Firm tagline", form.firm_tagline || "—"],
          ["Email", form.contact.email],
          ["Phone", form.contact.phone || "—"],
          ["Indiana architectural license no.", form.license_no],
          [
            "Office address",
            [
              form.postal_address.street,
              form.postal_address.city,
              form.postal_address.state,
              form.postal_address.postal_code,
            ]
              .filter(Boolean)
              .join(", ") || "—",
          ],
          ["Years practising", form.years_practising || "—"],
          ["Year founded", form.years_founded || "—"],
          ["Team size", form.team_size || "—"],
          [
            "Headshot",
            form.photo ? form.photo.original_name || "Uploaded" : "—",
          ],
        ]}
      />
      <ReviewSection
        title="Practice"
        step={2}
        onEdit={onEdit}
        rows={[
          ["Service areas", form.service_areas.join(", ") || "—"],
          ["Specialisations", form.specialisations.join(", ") || "—"],
          [
            "Lifestyle focuses",
            (form.lifestyle_focuses || []).join(", ") || "—",
          ],
          ["Demographic flags", form.demographic_flags.join(", ") || "—"],
        ]}
      >
        <div style={{ marginTop: 12 }}>
          <div className="wn-label">Practice statement</div>
          <p
            style={{
              marginTop: 4,
              color: "#272636",
              fontSize: 14,
              lineHeight: 1.55,
            }}
          >
            {form.practice_statement}
          </p>
        </div>
        {form.bio && (
          <div style={{ marginTop: 12 }}>
            <div className="wn-label">Biography</div>
            <p
              style={{
                marginTop: 4,
                color: "#272636",
                fontSize: 14,
                lineHeight: 1.55,
              }}
            >
              {form.bio}
            </p>
          </div>
        )}
      </ReviewSection>
      <ReviewSection title="Credentials & documents" step={3} onEdit={onEdit}>
        {(form.credentials || []).filter((c) => c.abbr || c.name).length > 0 && (
          <div style={{ marginTop: 4 }}>
            <div className="wn-label">Credentials</div>
            <ul
              style={{
                marginTop: 6,
                color: "#272636",
                fontSize: 13,
                lineHeight: 1.55,
                paddingLeft: 0,
                listStyle: "none",
              }}
              data-testid="review-credentials"
            >
              {form.credentials
                .filter((c) => c.abbr || c.name)
                .map((c, i) => (
                  <li key={i}>
                    <span
                      className="tabular-nums"
                      style={{ fontWeight: 700, marginRight: 8 }}
                    >
                      {c.abbr || "—"}
                    </span>
                    {c.name || ""}
                    {c.year ? (
                      <span
                        className="tabular-nums"
                        style={{ color: "#6C6B85", marginLeft: 8 }}
                      >
                        {c.year}
                      </span>
                    ) : null}
                  </li>
                ))}
            </ul>
          </div>
        )}
        {(form.standard_credentials || []).length > 0 && (
          <div style={{ marginTop: 4, marginBottom: 12 }}>
            <div className="wn-label">Green-building credentials</div>
            <div
              className="flex flex-wrap gap-2"
              style={{ marginTop: 6 }}
              data-testid="review-standard-credentials"
            >
              {form.standard_credentials.map((k) => {
                const item = STANDARD_CREDENTIALS.find((s) => s.key === k);
                return (
                  <span
                    key={k}
                    style={{
                      padding: "4px 10px",
                      borderRadius: 999,
                      background: "rgba(177,78,47,0.10)",
                      color: "#8F3C23",
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    {item ? item.label : k}
                  </span>
                );
              })}
            </div>
          </div>
        )}
        <div className="space-y-2 mt-3">
          {form.eo_doc && (
            <UploadedRow file={form.eo_doc} testidPrefix="review-eo" />
          )}
          {form.professional_liability_doc && (
            <UploadedRow
              file={form.professional_liability_doc}
              testidPrefix="review-prof-liability"
            />
          )}
        </div>
        <div
          className="mt-4"
          style={{
            padding: "10px 14px",
            border: "1px solid #D9D8E4",
            borderRadius: 4,
            background: "#FBFAFD",
            fontSize: 12.5,
            color: "#272636",
            lineHeight: 1.6,
          }}
          data-testid="review-registration-fee"
        >
          <span
            className="wn-label"
            style={{ color: "#8F3C23", marginRight: 8 }}
          >
            Registration fee
          </span>
          <span className="tabular-nums" style={{ fontWeight: 700 }}>
            ${REGISTRATION_FEE_USD}
          </span>{" "}
          — invoiced after approval.
        </div>
      </ReviewSection>
    </div>
  );
}

function ReviewSection({ title, step, onEdit, rows, children }) {
  return (
    <div
      className="wn-card mt-5"
      style={{ padding: "20px 22px" }}
      data-testid={`review-section-${step}`}
    >
      <div className="flex items-start justify-between">
        <h3
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontWeight: 600,
            fontSize: 19,
            color: "#1B1F4B",
          }}
        >
          {title}
        </h3>
        <button
          type="button"
          className="wn-btn wn-btn-tertiary"
          onClick={() => onEdit(step)}
          data-testid={`review-edit-${step}`}
        >
          Edit
        </button>
      </div>
      {rows && (
        <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3 mt-3">
          {rows.map(([k, v]) => (
            <div key={k}>
              <dt className="wn-label">{k}</dt>
              <dd
                style={{
                  marginTop: 2,
                  color: "#272636",
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                {v || "—"}
              </dd>
            </div>
          ))}
        </dl>
      )}
      {children}
    </div>
  );
}

function ReviewNotePanel({ note }) {
  return (
    <div
      className="wn-card mb-6"
      style={{
        padding: "14px 18px",
        borderLeft: "3px solid #B98A3C",
        backgroundColor: "rgba(185,138,60,0.06)",
      }}
      data-testid="resubmit-review-note"
    >
      <div className="wn-label" style={{ color: "#8a651f" }}>
        Reviewer note
      </div>
      <p
        style={{
          marginTop: 6,
          color: "#272636",
          fontSize: 14,
          lineHeight: 1.6,
        }}
      >
        {note}
      </p>
    </div>
  );
}
